import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { toast } from 'react-hot-toast';
import { usePlatform } from '../context/PlatformContext';
import Modal from '../components/ui/Modal';
import StatusBadge from '../components/ui/StatusBadge';
import Badge from '../components/ui/Badge';
import QRCodeDisplay from '../components/ui/QRCodeDisplay';
import { getVerificationURL } from '../utils/qrGenerator';
import { downloadCertificatePDF } from '../utils/pdfGenerator';
import confetti from 'canvas-confetti';
import {
  FiDownload, FiShare2, FiShield, FiExternalLink, FiAward, FiUser,
  FiEye, FiEyeOff, FiLink, FiCopy, FiLogOut, FiLock, FiMail, FiStar,
  FiGrid, FiList, FiClock
} from 'react-icons/fi';

export default function Portal() {
  const { candidates, allData, settings, allSettings, studentSession, setStudentSession, wallet, updatePrivacySetting } = usePlatform();
  const [loginEmail, setLoginEmail] = useState('');
  const [loginOTP, setLoginOTP] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [activeTab, setActiveTab] = useState('wallet');
  const [shareModal, setShareModal] = useState(null);

  const SIMULATED_OTP = '123456';

  const sendOTP = () => {
    if (!loginEmail.includes('@')) return toast.error('Enter a valid email address.');
    setOtpSent(true);
    toast.success(`OTP sent to ${loginEmail} (use: ${SIMULATED_OTP} for demo)`);
  };

  const verifyOTP = () => {
    if (loginOTP !== SIMULATED_OTP) return toast.error('Invalid OTP. Try: ' + SIMULATED_OTP);
    // Find candidate matching email
    const allCandidates = [
      ...(allData.internship?.candidates || []),
      ...(allData.certificate?.candidates || []),
    ];
    const found = allCandidates.find(c => c.email?.toLowerCase() === loginEmail.toLowerCase());
    setStudentSession({ email: loginEmail, name: found?.name || loginEmail.split('@')[0], candidateId: found?.id });
    toast.success(`Welcome, ${found?.name || loginEmail.split('@')[0]}! 🎉`);
  };

  const handleShare = () => {
    confetti({ particleCount: 100, spread: 70, origin: { y: 0.6 }, colors: ['#0EA5E9', '#3B82F6', '#8B5CF6'] });
  };

  if (!studentSession) {
    return (
      <div className="max-w-md mx-auto mt-10">
        <div className="glass-card rounded-[32px] p-10 text-center space-y-6">
          <div className="w-16 h-16 rounded-2xl bg-sky-50 flex items-center justify-center mx-auto">
            <FiLock className="w-8 h-8 text-sky-500" />
          </div>
          <div>
            <h1 className="text-2xl font-semibold text-navy-900">Student Portal</h1>
            <p className="text-slate-400 text-sm mt-2">Access your credentials, certificates, and digital wallet.</p>
          </div>
          {!otpSent ? (
            <div className="space-y-4">
              <input value={loginEmail} onChange={e => setLoginEmail(e.target.value)} type="email" placeholder="your@email.com"
                className="w-full border border-slate-200 rounded-2xl px-4 py-3.5 text-sm focus:outline-none focus:border-sky-400 transition-all text-center" />
              <button onClick={sendOTP} className="w-full py-3.5 bg-sky-600 text-white rounded-2xl font-black text-sm hover:bg-sky-500 transition-all shadow-lg shadow-sky-500/20">
                Send OTP
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="p-3 bg-sky-50 rounded-xl border border-sky-100 text-sm text-sky-700 font-bold">OTP sent to {loginEmail}</div>
              <input value={loginOTP} onChange={e => setLoginOTP(e.target.value)} type="text" maxLength={6} placeholder="Enter 6-digit OTP"
                className="w-full border border-slate-200 rounded-2xl px-4 py-3.5 text-sm focus:outline-none focus:border-sky-400 transition-all text-center font-mono text-2xl tracking-widest" />
              <button onClick={verifyOTP} className="w-full py-3.5 bg-slate-900 text-white rounded-2xl font-black text-sm hover:bg-slate-800 transition-all">
                Verify & Login
              </button>
              <button onClick={() => { setOtpSent(false); setLoginOTP(''); }} className="text-xs text-slate-800 font-semibold hover:text-slate-600">Change email</button>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Collect this student's credentials
  const allCandidates = [
    ...(allData.internship?.candidates || []).map(c => ({ ...c, mode: 'internship' })),
    ...(allData.certificate?.candidates || []).map(c => ({ ...c, mode: 'certificate' })),
  ];
  const myCreds = allCandidates.filter(c =>
    c.email?.toLowerCase() === studentSession.email.toLowerCase() && c.docStatus === 'generated'
  );

  const tabs = [
    { id: 'wallet', label: 'My Credentials', icon: FiAward },
    { id: 'badges', label: 'Badges', icon: FiStar },
    { id: 'timeline', label: 'Timeline', icon: FiClock },
    { id: 'profile', label: 'Profile', icon: FiUser },
  ];

  return (
    <div className="max-w-5xl mx-auto space-y-8 pb-16">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
        <div className="flex items-center gap-6">
          <div className="w-16 h-16 rounded-2xl bg-gradient-to-tr from-sky-400 to-blue-600 flex items-center justify-center shadow-lg shadow-sky-200">
            <span className="text-2xl font-black text-white">{studentSession.name[0].toUpperCase()}</span>
          </div>
          <div>
            <h1 className="page-title">Hello, {studentSession.name}!</h1>
            <p className="text-slate-800 font-bold mt-1">Your Digital Credential Vault</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex gap-1 bg-surface-100 p-1.5 rounded-xl">
            {tabs.map(t => (
              <button key={t.id} onClick={() => setActiveTab(t.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-bold transition-all ${activeTab === t.id ? 'bg-white text-brand-700 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}>
                <t.icon className="w-3.5 h-3.5" />{t.label}
              </button>
            ))}
          </div>
          <button onClick={() => { setStudentSession(null); toast.success('Logged out.'); }} className="w-10 h-10 flex items-center justify-center rounded-xl bg-white border border-slate-200 text-slate-400 hover:text-rose-500 hover:border-rose-200 transition-all">
            <FiLogOut className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Credentials Tab */}
      {activeTab === 'wallet' && (
        <div className="space-y-5">
          {myCreds.length === 0 ? (
            <div className="glass-card rounded-2xl p-12 text-center">
              <FiAward className="w-12 h-12 text-slate-200 mx-auto mb-4" />
              <p className="text-slate-900 font-bold">No credentials found for your account yet.</p>
            </div>
          ) : myCreds.map((cred, i) => {
            const isPublic = wallet.privacySettings[cred.id] !== false;
            const verifyUrl = getVerificationURL(cred.verificationId || cred.generatedId);
            return (
              <motion.div key={cred.id} initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.1 }}
                className="glass-card p-8 rounded-[32px] group">
                <div className="flex flex-col sm:flex-row justify-between gap-8">
                  <div className="flex gap-6">
                    <div className="w-16 h-16 rounded-2xl bg-slate-50 border border-slate-100 flex items-center justify-center text-slate-400 group-hover:text-sky-500 group-hover:bg-sky-50 transition-all">
                      <FiAward className="w-8 h-8" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="text-xl font-semibold text-navy-900 tracking-tight">{cred.name}</h3>
                        <StatusBadge status={cred.certStatus || 'active'} />
                        <button onClick={() => updatePrivacySetting(cred.id, !isPublic)} className={`p-1.5 rounded-lg transition-all ${isPublic ? 'text-emerald-500 bg-emerald-50' : 'text-slate-400 bg-slate-100'}`} title={isPublic ? 'Public' : 'Private'}>
                          {isPublic ? <FiEye className="w-3.5 h-3.5" /> : <FiEyeOff className="w-3.5 h-3.5" />}
                        </button>
                      </div>
                      <div className="flex items-center gap-3 mb-3">
                        <span className="text-[11px] font-black text-slate-400 uppercase tracking-widest">{cred.mode === 'internship' ? 'Internship' : 'Certificate'}</span>
                        <span className="w-1 h-1 rounded-full bg-slate-300" />
                        <span className="text-[11px] font-semibold text-brand-600 uppercase tracking-widest">{new Date(cred.generatedAt || cred.createdAt).toLocaleDateString()}</span>
                      </div>
                      <div className="font-mono text-[11px] text-slate-700 font-semibold bg-slate-50 px-3 py-1 rounded-lg inline-block mb-3">ID: {cred.generatedId}</div>
                      <div className="flex flex-wrap gap-2">
                        <button onClick={() => { navigator.clipboard.writeText(verifyUrl); toast.success('Verify link copied!'); }}
                          className="flex items-center gap-1.5 px-3 py-1.5 bg-slate-100 text-slate-600 rounded-lg text-[10px] font-bold hover:bg-slate-200 transition-all">
                          <FiLink className="w-3 h-3" /> Copy Verify Link
                        </button>
                        <button onClick={() => {
                          const linkedIn = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(verifyUrl)}`;
                          window.open(linkedIn, '_blank');
                          handleShare();
                        }} className="flex items-center gap-1.5 px-3 py-1.5 bg-[#0077b5]/10 text-[#0077b5] border border-[#0077b5]/20 rounded-lg text-[10px] font-bold hover:bg-[#0077b5]/20 transition-all">
                          LinkedIn Share
                        </button>
                        <button onClick={() => setShareModal(cred)} className="flex items-center gap-1.5 px-3 py-1.5 bg-sky-50 text-sky-600 border border-sky-100 rounded-lg text-[10px] font-bold hover:bg-sky-100 transition-all">
                          <FiShare2 className="w-3 h-3" /> Share
                        </button>
                      </div>
                    </div>
                  </div>
                  <div className="flex sm:flex-col gap-3">
                    <button onClick={() => downloadCertificatePDF(cred, allSettings[cred.mode] || settings, cred.mode).then(() => toast.success('PDF downloaded!'))}
                      className="flex items-center gap-2 px-5 py-2.5 bg-slate-900 text-white rounded-xl text-xs font-bold hover:bg-slate-800 transition-all">
                      <FiDownload /> PDF
                    </button>
                    <a href={`#/verify/${cred.verificationId || cred.generatedId}`} target="_blank" rel="noopener noreferrer"
                      className="flex items-center gap-2 px-5 py-2.5 bg-sky-600 text-white rounded-xl text-xs font-bold hover:bg-sky-500 transition-all">
                      <FiShield /> Verify
                    </a>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      )}

      {/* Badges Tab */}
      {activeTab === 'badges' && (
        <div className="glass-card rounded-2xl p-8">
          <h2 className="text-xl font-semibold text-navy-900 mb-6">My Digital Badges</h2>
          <div className="grid grid-cols-3 md:grid-cols-6 gap-6">
            {wallet.badges.map(badge => <Badge key={badge.id} type={badge.type} label={badge.label} sublabel={badge.sublabel} size={80} />)}
          </div>
        </div>
      )}

      {/* Timeline Tab */}
      {activeTab === 'timeline' && (
        <div className="glass-card rounded-2xl p-8">
          <h2 className="text-xl font-semibold text-navy-900 mb-6">Credential Timeline</h2>
          {myCreds.length === 0 ? <p className="text-slate-400 text-center py-8">No credentials yet.</p> : (
            <div className="relative">
              <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-slate-100" />
              <div className="space-y-6">
                {[...myCreds].sort((a, b) => new Date(b.generatedAt || b.createdAt) - new Date(a.generatedAt || a.createdAt)).map((cred, i) => (
                  <div key={cred.id} className="flex gap-6 items-start pl-2">
                    <div className="w-8 h-8 rounded-full bg-sky-500 flex items-center justify-center flex-shrink-0 relative z-10 shadow-lg shadow-sky-200">
                      <FiAward className="w-4 h-4 text-white" />
                    </div>
                    <div className="flex-1 pb-4 border-b border-surface-50">
                      <div className="font-semibold text-navy-900">{cred.mode === 'internship' ? 'Internship' : 'Certificate'} Issued</div>
                      <div className="text-sm text-slate-500 mt-0.5">{cred.name} · {cred.generatedId}</div>
                      <div className="text-[10px] text-slate-700 font-bold font-bold uppercase tracking-widest mt-1">{new Date(cred.generatedAt || cred.createdAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Profile Tab */}
      {activeTab === 'profile' && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="glass-card rounded-2xl p-8 space-y-4">
            <h2 className="text-xl font-semibold text-navy-900">Profile</h2>
            <div className="space-y-3">
              <div className="flex justify-between text-sm"><span className="text-slate-400">Name</span><span className="font-bold text-slate-800">{studentSession.name}</span></div>
              <div className="flex justify-between text-sm"><span className="text-slate-400">Email</span><span className="font-bold text-slate-800">{studentSession.email}</span></div>
              <div className="flex justify-between text-sm"><span className="text-slate-400">Credentials</span><span className="font-bold text-sky-600">{myCreds.length}</span></div>
              <div className="flex justify-between text-sm"><span className="text-slate-400">Badges</span><span className="font-bold text-violet-600">{wallet.badges.length}</span></div>
            </div>
            <button onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/#/portal?student=${studentSession.email}`); toast.success('Profile link copied!'); }}
              className="w-full flex items-center justify-center gap-2 py-3 bg-slate-900 text-white rounded-xl text-xs font-bold hover:bg-slate-800 transition-all mt-4">
              <FiCopy /> Copy Public Profile Link
            </button>
          </div>
          <div className="glass-card rounded-2xl p-8 space-y-4">
            <h2 className="text-xl font-semibold text-navy-900">QR Profile</h2>
            <p className="text-slate-400 text-sm">Share this QR to let others view your credential profile.</p>
            <div className="flex justify-center">
              <QRCodeDisplay value={`${window.location.origin}/#/portal?student=${studentSession.email}`} size={160} />
            </div>
          </div>
        </div>
      )}

      {/* Share Modal */}
      <Modal isOpen={!!shareModal} onClose={() => setShareModal(null)} title="Share Credential">
        {shareModal && (
          <div className="p-6 space-y-4">
            <div className="flex justify-center">
              <QRCodeDisplay value={getVerificationURL(shareModal.verificationId || shareModal.generatedId)} size={180} />
            </div>
            <div className="space-y-2">
              {[
                { label: 'LinkedIn', url: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(getVerificationURL(shareModal.verificationId || shareModal.generatedId))}`, color: 'bg-[#0077b5] text-white' },
                { label: 'Twitter / X', url: `https://twitter.com/intent/tweet?url=${encodeURIComponent(getVerificationURL(shareModal.verificationId || shareModal.generatedId))}&text=Check+out+my+verified+credential!`, color: 'bg-slate-900 text-white' },
                { label: 'WhatsApp', url: `https://wa.me/?text=${encodeURIComponent('My verified credential: ' + getVerificationURL(shareModal.verificationId || shareModal.generatedId))}`, color: 'bg-[#25D366] text-white' },
              ].map(s => (
                <a key={s.label} href={s.url} target="_blank" rel="noopener noreferrer" className={`flex items-center justify-between px-5 py-3 rounded-xl font-bold text-sm ${s.color} hover:opacity-90 transition-all`}>
                  {s.label}<FiExternalLink />
                </a>
              ))}
              <button onClick={() => { navigator.clipboard.writeText(getVerificationURL(shareModal.verificationId || shareModal.generatedId)); toast.success('Link copied!'); }}
                className="w-full flex items-center justify-between px-5 py-3 bg-slate-100 text-slate-700 rounded-xl font-bold text-sm hover:bg-slate-200 transition-all">
                Copy Direct Link <FiCopy />
              </button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}